import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { ButtonAdd } from '../../../components/Buttons';
import { FormContainer, FormInput, FormRow, Label } from '../../../components/form';
import { LineDivider } from '../../../components/LineDivider';
import { getFormErrorMessage } from '../../../utils/errors';
import {
    RHFInputNumber,
    RHFMultiSelect,
    RHFYearCalendar
} from '../../../components/rhf';
import { IconTooltip } from '../../../components/IconTooltip';
import { MAX_TERM_LENGTH } from '../../../constants/constants';
import { TermMemberTable } from './TermMemberTable';
import { LeadershipTable } from './LeadershipTable';
import { AdditionalTermMemberDialog } from './AdditionalTermMemberDialog';
import { LeadershipFormDialog } from './LeadershipFormDialog';
import { formatMemberName } from '../../../../utils/texts';

export const TermAddForm = ({
    members,
    politicalEntities,
    onSubmit
}) => {
    const [processing, setProcessing] = useState(false);
    const [addedMemberIds, setAddedMemberIds] = useState(new Set());
    const [leadership, setLeadership] = useState([]);
    const [leadershipMember, setLeadershipMember] = useState(null);
    const [showLeadershipFormDialog, setShowLeadershipFormDialog] = useState(false);
    const [isVisibleAddMembersDialog, setIsVisibleAddMembersDialog] = useState(false);

    const filteredMembers = useMemo(
        () => members.filter((member) => !addedMemberIds.has(member.id)),
        [members, addedMemberIds]
    );

    const defaultValues = {
        year_from: null,
        year_to: null,
        order: null,
        coalition: [],
        members: [],
        leadership: []
    };

    const {
        control,
        formState: { errors, isSubmitting },
        handleSubmit,
        getValues
    } = useForm({
        disabled: processing,
        defaultValues
    });

    const { fields, append, remove } = useFieldArray({ control, name: 'members' });

    useEffect(() => {
        setProcessing(isSubmitting);
    }, [isSubmitting]);

    const leaderOptions = useMemo(() => {
        return fields.map((item) => ({
            id: item.member_id,
            label: formatMemberName(item.first_name, item.last_name)
        }));
    }, [fields]);

    const handleAddMembers = (selectedMembers) => {
        const newAddedMembers = [];
        const newAddedMemberIds = new Set(addedMemberIds);
        selectedMembers.forEach((member) => {
            newAddedMembers.push({
                member_id: member.id,
                first_name: member.first_name,
                last_name: member.last_name
            });
            newAddedMemberIds.add(member.id);
        });
        setAddedMemberIds(newAddedMemberIds);
        newAddedMembers.forEach((member) => append(member));
        hideAddMembersDialog();
    };

    const handleRemoveMember = (item, index) => {
        const newAddedMemberIds = new Set(addedMemberIds);
        newAddedMemberIds.delete(item.member_id);
        setAddedMemberIds(newAddedMemberIds);
        remove(index);
    };

    const showAddMembersDialog = () => {
        setIsVisibleAddMembersDialog(true)
    };
    
    const hideAddMembersDialog = () => {
        setIsVisibleAddMembersDialog(false)
    };

    const handleClickAddRoles = () => {
        setLeadershipMember(null);
        setShowLeadershipFormDialog(true);
    };

    const handleClickEditRoles = (member) => {
        setLeadershipMember(member);
        setShowLeadershipFormDialog(true);
    };

    const hideLeadershipDialog = () => {
        setLeadershipMember(null);
        setShowLeadershipFormDialog(false);
    };

    const handleSubmitLeadershipForm = (data) => {
        let updatedLeadership = [...leadership];
        const exists = updatedLeadership.some((m) => m.id === data.member);

        if (exists) {
            updatedLeadership = updatedLeadership.map((m) =>
                m.id === data.member
                    ? { ...m, leadership_roles: data.leadership_roles }
                    : m
            );
        } else {
            const member = members.find((m) => m.id === data.member);
            updatedLeadership.push({
                id: member.id,
                first_name: member.first_name,
                last_name: member.last_name,
                leadership_roles: data.leadership_roles
            });
        }

        updatedLeadership = updatedLeadership.filter(
            (m) => m.leadership_roles?.length > 0
        );

        setLeadership(updatedLeadership);
        hideLeadershipDialog();
    };

    return (
        <>
            <form onSubmit={handleSubmit((data) => onSubmit(data, leadership))}>
                <FormContainer>
                    <FormRow>
                        <FormInput>
                            <Label htmlFor='year_from' text='Začátek' required />
                            <RHFYearCalendar
                                control={control}
                                name='year_from'
                                rules={{ required: 'Zadejte začátek funkčního období' }}
                                placeholder='Rok od'
                            />
                            {getFormErrorMessage(errors, 'year_from')}
                        </FormInput>
                        <FormInput>
                            <Label htmlFor='year_to' text='Konec' required />
                            <RHFYearCalendar
                                control={control}
                                name='year_to'
                                rules={{
                                    required: 'Zadejte konec funkčního období',
                                    validate: {
                                        order: (value, formValues) => {
                                            const start = formValues.year_from;
                                            const end = value;
                                            if (!start || !end) return;
                                            if (start.getFullYear() > end.getFullYear()) {
                                                return 'Konec funkčního období musí být později než začátek';
                                            }
                                        },
                                        duration: (value, formValues) => {
                                            const start = formValues.year_from;
                                            const end = value;
                                            if (!start || !end) return;
                                            const termLength = end.getFullYear() - start.getFullYear();
                                            if (termLength > MAX_TERM_LENGTH) {
                                                return `Funkční období může trvat maximálně ${MAX_TERM_LENGTH} roky`;
                                            }
                                        }
                                    }
                                }}
                                placeholder='Rok do'
                            />
                            {getFormErrorMessage(errors, 'year_to')}
                        </FormInput>
                    </FormRow>
                    <FormRow>
                        <FormInput>
                            <Label
                                htmlFor='order'
                                text='Pořadí'
                                warning='Pořadové číslo zasedání nelze po vytvoření funkčního období změnit. Pokud nebude
                                    zadáno, automaticky se použije hodnota následující po nejvyšším aktuálním pořadí.'
                            />
                            <RHFInputNumber
                                control={control}
                                name='order'
                                inputProps={{ min: 1 }}
                            />
                        </FormInput>
                        <FormInput>
                            <Label htmlFor='coalition' text='Koalice' />
                            <RHFMultiSelect
                                control={control}
                                name='coalition'
                                placeholder='Koaliční subjekty'
                                inputProps={{
                                    options: politicalEntities,
                                    optionLabel: 'abbreviation',
                                    optionValue: 'id'
                                }}
                            />
                        </FormInput>
                    </FormRow>
                    <LineDivider />
                    <FormRow type='heading'>
                        Členové pro funkční období
                        <IconTooltip>
                            Členové, kteří byli aktivní na začátku funkčního období. Další mohou být průběžně přidáváni
                            s jednotlivými zasedáními.
                        </IconTooltip>
                    </FormRow>
                    <FormRow>
                        <span className='form-input__info-text'>
                            Celkem zvoleno členů: <strong>{fields.length}</strong>
                        </span>
                        <ButtonAdd
                            type='button'
                            label='Přidat členy'
                            icon='bi bi-person-plus'
                            onClick={showAddMembersDialog}
                            disabled={processing}
                            loading={processing}
                        />
                    </FormRow>
                    <FormRow>
                        <TermMemberTable
                            control={control}
                            errors={errors}
                            fields={fields}
                            getValues={getValues}
                            politicalEntities={politicalEntities}
                            disabled={processing}
                            onRemoveMember={handleRemoveMember}
                        />
                    </FormRow>
                    <LineDivider />
                    <FormRow type='heading'>
                        Vedení pro funkční období
                        <IconTooltip>
                            Členové z výše zvolených, kteří jsou součástí vedení orgánu.
                        </IconTooltip>
                    </FormRow>
                    <FormRow>
                        <ButtonAdd
                            type='button'
                            label='Přidat vedení'
                            icon='bi bi-person-plus'
                            onClick={handleClickAddRoles}
                            disabled={processing}
                            loading={processing}
                        />
                    </FormRow>
                    <FormRow>
                        <LeadershipTable
                            control={control}
                            errors={errors}
                            leadership={leadership}
                            disabled={processing}
                            onClickEditRoles={handleClickEditRoles}
                            renderAddRolesButton={false}
                        />
                    </FormRow>

                    <LineDivider />

                    <FormRow type='button'>
                        <ButtonAdd
                            type='submit'
                            label='Přidat funkční období'
                            disabled={processing}
                            loading={processing}
                        />
                    </FormRow>
                </FormContainer>
            </form>

            <AdditionalTermMemberDialog
                members={filteredMembers}
                isVisible={isVisibleAddMembersDialog}
                onHide={hideAddMembersDialog}
                onConfirm={handleAddMembers}
            />

            <LeadershipFormDialog
                members={leaderOptions
                }
                leadership={leadership}
                editMember={leadershipMember}
                isVisible={showLeadershipFormDialog}
                onHide={hideLeadershipDialog}
                onSubmit={handleSubmitLeadershipForm}
            />
        </>
    );
};
